// Admin console.
import {
  IS_LOCAL_DEVELOPMENT,
  SUPABASE_KEY,
  SUPABASE_PROJECT_NAME,
  SUPABASE_URL
} from "../config.js";
import {
  loadAdminBetaRequests,
  loadAdminStatistics,
  loadSupabaseUsageSnapshot
} from "../data/admin.js";
import {
  bindCurriculumBrowser,
  canonicalExerciseDescriptor,
  curriculumBrowser,
  dedupeCanonicalExerciseRecords,
  exerciseQuestionCount
} from "../domain/curriculum.js";
import { importExercisesModal } from "../features/exercise-authoring.js";
import { bindLanguage, copy, languageControl, t } from "../i18n.js";
import { readApiResponse, readableError } from "../lib/api.js";
import { app, closeModal, escapeHTML, setBusy, showModal, showToast } from "../lib/dom.js";
import { betaRequestStatusLabel, moneyCents, moneyEUR } from "../lib/format.js";
import { logout } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { adminCostsView } from "./admin-costs.js";

function adminNavigation() {
  const items = [
    ["dashboard", "⌂", state.language === "it" ? "Panoramica" : "Overview", state.language === "it" ? "Stato generale" : "General status"],
    ["activity", "↗", state.language === "it" ? "Utilizzo" : "Usage", state.language === "it" ? "Connessioni e AI" : "Connections and AI"],
    ["users", "👥", state.language === "it" ? "Utenti" : "Users", state.language === "it" ? "Ruoli e crediti" : "Roles and credits"],
    ["beta", "✉", state.language === "it" ? "Accessi beta" : "Beta access", state.language === "it" ? "Richieste e inviti" : "Requests and invitations"],
    ["exercises", "✎", state.language === "it" ? "Esercizi" : "Exercises", state.language === "it" ? "Catalogo master" : "Master catalogue"],
    ["costs", "€", state.language === "it" ? "Costi" : "Costs", state.language === "it" ? "Servizi e review" : "Services and review"]
  ];
  return `<nav class="nav-list admin-nav-list">${items.map(([tab, icon, label, hint]) => `
    <button class="nav-button ${state.adminTab === tab ? "active" : ""}" data-admin-tab="${tab}">
      <span class="nav-icon">${icon}</span>
      <span><span class="nav-label">${label}</span><small>${hint}</small></span>
    </button>
  `).join("")}</nav>`;
}

function adminPageTitle() {
  const titles = {
    dashboard: [state.language === "it" ? "Panoramica" : "Overview", state.language === "it" ? "Le informazioni principali, senza dettagli tecnici inutili." : "The main information, without unnecessary technical details."],
    activity: [state.language === "it" ? "Utilizzo applicazione" : "Application usage", state.language === "it" ? "Connessioni, traffico e uso AI." : "Connections, traffic and AI usage."],
    users: [state.language === "it" ? "Utenti" : "Users", state.language === "it" ? "Ruoli, crediti e ultime operazioni." : "Roles, credits and latest operations."],
    beta: [state.language === "it" ? "Accessi beta" : "Beta access", state.language === "it" ? "Approva richieste e controlla gli inviti personali." : "Approve requests and manage personal invitations."],
    exercises: [state.language === "it" ? "Esercizi" : "Exercises", state.language === "it" ? "Catalogo didattico e import master." : "Learning catalogue and master import."],
    costs: [state.language === "it" ? "Costi" : "Costs", state.language === "it" ? "Servizi collegati, costi registrati e richieste da revisionare." : "Connected services, recorded costs and review items."]
  };
  return titles[state.adminTab] || titles.dashboard;
}

function adminConnectionChart() {
  const maxCount = Math.max(1, ...state.adminHourlyConnections.map(item => Number(item.connection_count || 0)));
  const rows = state.adminHourlyConnections.length
    ? state.adminHourlyConnections.map(item => {
      const height = Math.max(8, Math.round((Number(item.connection_count || 0) / maxCount) * 130));
      const date = new Date(item.bucket_start);
      return `<div class="chart-bar" title="${date.toLocaleString(state.language)} · ${item.connection_count} connessioni · ${item.unique_users} utenti">
        <span style="height:${height}px"></span>
        <small>${String(item.hour_of_day).padStart(2, "0")}</small>
      </div>`;
    }).join("")
    : `<p style="color:var(--muted)">${state.language === "it" ? "Le connessioni verranno tracciate da ora in avanti." : "Connections will be tracked from now on."}</p>`;
  return `<div class="admin-chart">${rows}</div>`;
}

function adminKpi(label, value, tone = "") {
  return `<div class="admin-kpi ${tone}">
    <strong>${value}</strong>
    <span>${label}</span>
  </div>`;
}

export function adminWorkspaceHero({ icon, eyebrow, title, description, metrics = [] }) {
  return `<section class="admin-hero card admin-section-hero">
    <div class="admin-section-hero-copy">
      <span class="admin-hero-icon" aria-hidden="true">${icon}</span>
      <div><span class="eyebrow">${escapeHTML(eyebrow)}</span><h2>${escapeHTML(title)}</h2><p>${escapeHTML(description)}</p></div>
    </div>
    <div class="admin-hero-metrics">${metrics.map(metric => adminKpi(metric.label, metric.value, metric.tone || "")).join("")}</div>
  </section>`;
}

function adminDashboardView() {
  const summary = state.adminDashboard || {};
  const canonicalCatalogueCount = dedupeCanonicalExerciseRecords(state.adminExercises.filter(exercise => exercise.is_catalog !== false)).length;
  return `
    <section class="admin-hero card admin-control-hero">
      <div>
        <span class="eyebrow">${state.language === "it" ? "Stato applicazione" : "Application status"}</span>
        <h2>${state.language === "it" ? "BrainMath sotto controllo" : "BrainMath at a glance"}</h2>
        <p>${state.language === "it" ? "Numeri principali, uso settimanale e costi operativi in un’unica vista." : "Main numbers, weekly usage and operating costs in one view."}</p>
      </div>
      <div class="admin-hero-metrics">
        ${adminKpi(state.language === "it" ? "connessioni 7g" : "7d connections", Number(summary.app_connections_7d || 0).toLocaleString(), "accent")}
        ${adminKpi(state.language === "it" ? "utenti unici 7g" : "7d unique users", Number(summary.unique_users_7d || 0).toLocaleString())}
        ${adminKpi(state.language === "it" ? "costi mese" : "month costs", moneyEUR(summary.platform_cost_eur_month), "warn")}
      </div>
    </section>
    <section class="admin-kpi-grid">
      ${adminKpi("Account", Number(summary.total_accounts || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Bambini" : "Children", Number(summary.child_profiles || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Genitori" : "Parents", Number(summary.parent_accounts || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Insegnanti" : "Teachers", Number(summary.teacher_accounts || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Classi" : "Classes", Number(summary.classes_count || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Esercizi catalogo" : "Catalogue exercises", canonicalCatalogueCount.toLocaleString())}
      ${adminKpi("AI token", Number(summary.ai_total_tokens || 0).toLocaleString())}
      ${adminKpi(state.language === "it" ? "Costo AI" : "AI cost", `€${Number(summary.ai_cost_eur || 0).toFixed(4)}`, "warn")}
    </section>
    <section class="admin-dashboard-grid">
      <div class="card admin-side-card">
        <h2>${state.language === "it" ? "Cosa guardare oggi" : "What to check today"}</h2>
        <div class="compact-stack">
          <div class="compact-row"><strong>${state.language === "it" ? "Utenti attivi" : "Active users"}</strong><span>${Number(summary.unique_users_7d || 0).toLocaleString()} ${state.language === "it" ? "ultimi 7 giorni" : "last 7 days"}</span></div>
          <div class="compact-row"><strong>${state.language === "it" ? "Costo mese" : "Month cost"}</strong><span>${moneyEUR(summary.platform_cost_eur_month)}</span></div>
          <div class="compact-row"><strong>${state.language === "it" ? "Esercizi disponibili" : "Available exercises"}</strong><span>${canonicalCatalogueCount.toLocaleString()}</span></div>
        </div>
      </div>
      <div class="card admin-side-card">
        <h2>${state.language === "it" ? "Scorciatoie" : "Shortcuts"}</h2>
        <div class="quick-actions">
          <button class="secondary" data-admin-tab="activity">${state.language === "it" ? "Vedi utilizzo" : "View usage"}</button>
          <button class="secondary" data-admin-tab="users">${state.language === "it" ? "Controlla utenti" : "Check users"}</button>
          <button class="secondary" data-admin-tab="costs">${state.language === "it" ? "Registra costi" : "Record costs"}</button>
        </div>
      </div>
    </section>`;
}

function adminActivityView() {
  const aiSummary = state.adminSummary || {};
  const dailyCards = state.adminDailyUsage.slice(0, 8).map(day => `
    <div class="compact-row">
      <strong>${escapeHTML(day.usage_date)}</strong>
      <span>${day.request_count} ${state.language === "it" ? "richieste" : "requests"} · ${Number(day.total_tokens).toLocaleString()} token · €${Number(day.cost_eur).toFixed(4)}</span>
    </div>`).join("");
  const usageRows = state.adminUsage.length ? state.adminUsage.map(usage => `
    <tr>
      <td>${new Date(usage.created_at).toLocaleString(state.language)}</td>
      <td>${escapeHTML(usage.username)}</td>
      <td>${escapeHTML(usage.exercise_title || "—")}</td>
      <td>${escapeHTML(usage.model)}</td>
      <td>${Number(usage.total_tokens).toLocaleString()}</td>
      <td>$${Number(usage.cost_usd).toFixed(4)}</td>
      <td>€${Number(usage.cost_eur).toFixed(4)}</td>
      <td>${usage.charged_credits}</td>
    </tr>`).join("") : `<tr><td colspan="8">—</td></tr>`;
  return `<div class="stack admin-activity-page">
    ${adminWorkspaceHero({
      icon: "↗",
      eyebrow: state.language === "it" ? "Monitoraggio operativo" : "Operational monitoring",
      title: state.language === "it" ? "Come viene usata BrainMath" : "How BrainMath is being used",
      description: state.language === "it" ? "Connessioni e richieste AI organizzate in una vista leggibile, dai dati generali fino al dettaglio." : "Connections and AI requests organised from the overall picture down to individual details.",
      metrics: [
        { label: state.language === "it" ? "richieste AI" : "AI requests", value: Number(aiSummary.request_count || 0).toLocaleString() },
        { label: "token", value: Number(aiSummary.total_tokens || 0).toLocaleString() },
        { label: state.language === "it" ? "costo AI" : "AI cost", value: `€${Number(aiSummary.total_cost_eur || 0).toFixed(4)}`, tone: "warn" }
      ]
    })}
    <div class="admin-dashboard-grid admin-activity-grid">
    <section class="card admin-wide-card">
      <div class="admin-card-heading"><span class="admin-card-icon">◫</span><div><span class="eyebrow">${state.language === "it" ? "Ultimi 7 giorni" : "Last 7 days"}</span><h2>${state.language === "it" ? "Connessioni per fascia oraria" : "Connections by hour"}</h2><p>${state.language === "it" ? "Visualizza rapidamente gli orari in cui l’applicazione viene utilizzata di più." : "Quickly identify the hours when the application is used most."}</p></div></div>
      ${adminConnectionChart()}
    </section>
    <aside class="card admin-side-card admin-insight-card">
      <div class="admin-card-heading compact"><span class="admin-card-icon">✦</span><div><h2>${state.language === "it" ? "Uso AI" : "AI usage"}</h2><p>${state.language === "it" ? "Sintesi economica e tecnica." : "Economic and technical summary."}</p></div></div>
      <div class="compact-stack">
        <div class="compact-row"><strong>${Number(aiSummary.request_count || 0)}</strong><span>${state.language === "it" ? "richieste generate" : "generated requests"}</span></div>
        <div class="compact-row"><strong>${Number(aiSummary.total_tokens || 0).toLocaleString()}</strong><span>token</span></div>
        <div class="compact-row"><strong>€${Number(aiSummary.total_cost_eur || 0).toFixed(4)}</strong><span>${state.language === "it" ? "costo stimato" : "estimated cost"}</span></div>
        <div class="compact-row"><strong>€${Number(aiSummary.average_cost_eur || 0).toFixed(4)}</strong><span>${state.language === "it" ? "media richiesta" : "avg request"}</span></div>
      </div>
    </aside>
    <section class="card admin-side-card admin-insight-card">
      <div class="admin-card-heading compact"><span class="admin-card-icon">▤</span><div><h2>${state.language === "it" ? "Ultimi giorni" : "Recent days"}</h2><p>${state.language === "it" ? "Andamento giornaliero delle richieste." : "Daily request trend."}</p></div></div>
      <div class="compact-stack">${dailyCards || `<p style="color:var(--muted)">—</p>`}</div>
    </section>
    <section class="card admin-full-card admin-data-table-card" style="overflow:auto">
      <div class="admin-card-heading"><span class="admin-card-icon">≡</span><div><span class="eyebrow">${state.language === "it" ? "Audit AI" : "AI audit"}</span><h2>${state.language === "it" ? "Dettaglio richieste AI" : "AI request details"}</h2><p>${state.language === "it" ? "Richieste generate dagli insegnanti con token, costo stimato e crediti addebitati." : "Teacher-generated requests with tokens, estimated cost and charged credits."}</p></div></div>
      <table class="admin-table">
        <thead><tr>
          <th>${state.language === "it" ? "Data" : "Date"}</th>
          <th>${state.language === "it" ? "Utente" : "User"}</th>
          <th>${state.language === "it" ? "Esercizio" : "Exercise"}</th>
          <th>Model</th><th>Token</th><th>USD</th><th>EUR</th><th>${state.language === "it" ? "Crediti" : "Credits"}</th>
        </tr></thead>
        <tbody>${usageRows}</tbody>
      </table>
    </section>
    </div>
  </div>`;
}

function adminUsersView() {
  const lowCreditUsers = state.adminUsers.filter(user => Number(user.wallet_balance || 0) <= 5).length;
  const teacherUsers = state.adminUsers.filter(user => String(user.roles || "").toLowerCase().includes("teacher") || String(user.roles || "").toLowerCase().includes("insegnante")).length;
  const purchaseUsers = state.adminUsers.filter(user => Boolean(user.last_purchase_at)).length;
  const rows = state.adminUsers.length ? state.adminUsers.map(user => `
    <tr>
      <td><strong>@${escapeHTML(user.username)}</strong><br><small>${new Date(user.created_at).toLocaleDateString(state.language)}</small></td>
      <td>${escapeHTML(user.roles || "—")}</td>
      <td>${escapeHTML(user.teacher_verification_status || "—")}</td>
      <td>${user.child_profile_count} / ${user.class_count}</td>
      <td>${user.wallet_balance ?? 0}</td>
      <td>${user.lifetime_purchased ?? 0} / ${user.lifetime_used ?? 0}</td>
      <td>${user.last_transaction_at ? `${new Date(user.last_transaction_at).toLocaleString(state.language)}<br><small>${escapeHTML(user.last_transaction_type || "")} · ${user.last_transaction_credits || 0}</small>` : "—"}</td>
      <td>${user.last_purchase_at ? `${new Date(user.last_purchase_at).toLocaleString(state.language)}<br><small>${moneyCents(user.last_purchase_amount_cents)} · ${escapeHTML(user.last_purchase_status || "")}</small>` : "—"}</td>
    </tr>`).join("") : `<tr><td colspan="8">—</td></tr>`;
  return `<div class="stack admin-users-page">
    ${adminWorkspaceHero({
      icon: "👥",
      eyebrow: state.language === "it" ? "Gestione account" : "Account management",
      title: state.language === "it" ? "Utenti, ruoli e crediti" : "Users, roles and credits",
      description: state.language === "it" ? "Una vista amministrativa unica per controllare accessi, ruoli, disponibilità crediti e acquisti." : "One administrative view for accounts, roles, credit availability and purchases.",
      metrics: [
        { label: "account", value: state.adminUsers.length.toLocaleString() },
        { label: state.language === "it" ? "insegnanti" : "teachers", value: teacherUsers.toLocaleString() },
        { label: state.language === "it" ? "saldo basso" : "low balance", value: lowCreditUsers.toLocaleString(), tone: lowCreditUsers ? "warn" : "accent" }
      ]
    })}
    <section class="admin-kpi-grid admin-user-summary-grid">
      ${adminKpi(state.language === "it" ? "Account totali" : "Total accounts", state.adminUsers.length.toLocaleString())}
      ${adminKpi(state.language === "it" ? "Account insegnante" : "Teacher accounts", teacherUsers.toLocaleString())}
      ${adminKpi(state.language === "it" ? "Con acquisti" : "With purchases", purchaseUsers.toLocaleString(), "accent")}
      ${adminKpi(state.language === "it" ? "Crediti da controllare" : "Credits to check", lowCreditUsers.toLocaleString(), lowCreditUsers ? "warn" : "")}
    </section>
    <section class="card admin-workspace-card admin-users-card" style="overflow:auto">
    <div class="workspace-section-title"><div><span class="eyebrow">${state.language === "it" ? "Anagrafica e transazioni" : "Accounts and transactions"}</span><h2>${state.language === "it" ? "Utenti e crediti" : "Users and credits"}</h2><p>${state.language === "it" ? "Controlla ruoli, disponibilità crediti, ultime transazioni e acquisti." : "Inspect roles, credit availability, latest transactions and purchases."}</p></div><span>${state.adminUsers.length} ${state.language === "it" ? "account" : "accounts"}</span></div>
    <table class="admin-table">
      <thead><tr>
        <th>Account</th><th>${state.language === "it" ? "Ruoli" : "Roles"}</th><th>Teacher</th>
        <th>${state.language === "it" ? "Figli / classi" : "Children / classes"}</th>
        <th>${state.language === "it" ? "Saldo" : "Balance"}</th><th>${state.language === "it" ? "Acq. / usati" : "Bought / used"}</th>
        <th>${state.language === "it" ? "Ultima transazione" : "Latest transaction"}</th>
        <th>${state.language === "it" ? "Ultimo acquisto" : "Latest purchase"}</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section></div>`;
}

function adminBetaAccessView() {
  const pending = state.adminBetaRequests.filter(item => item.status === "pending").length;
  const approved = state.adminBetaRequests.filter(item => item.status === "approved").length;
  const completed = state.adminBetaRequests.filter(item => Number(item.invite_use_count || 0) > 0).length;
  const refreshedAt = state.adminBetaRefreshedAt instanceof Date
    ? state.adminBetaRefreshedAt.toLocaleTimeString(state.language, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";
  const requestCards = state.adminBetaRequests.length
    ? state.adminBetaRequests.map(request => {
      const roles = (request.requested_roles || []).map(role => role === "parent"
        ? (state.language === "it" ? "Genitore" : "Parent")
        : (state.language === "it" ? "Insegnante" : "Teacher")).join(" · ");
      const inviteUsed = Number(request.invite_use_count || 0) > 0;
      const status = inviteUsed ? (state.language === "it" ? "Registrazione completata" : "Registration completed") : betaRequestStatusLabel(request.status);
      return `<article class="card beta-request-card">
        <div class="library-heading">
          <div>
            <span class="eyebrow">${escapeHTML(roles)}</span>
            <h2>${escapeHTML(request.first_name)} ${escapeHTML(request.last_name)}</h2>
            <p>${escapeHTML(request.email)} · ${new Date(request.created_at).toLocaleString(state.language)}</p>
          </div>
          <span class="status-pill">${escapeHTML(status)}</span>
        </div>
        ${request.school_name ? `<div class="compact-row"><strong>${state.language === "it" ? "Scuola" : "School"}</strong><span>${escapeHTML(request.school_name)} · ${escapeHTML(request.school_city || "")}</span></div>` : ""}
        ${request.motivation ? `<div class="beta-request-note"><strong>${state.language === "it" ? "Motivazione" : "Reason"}</strong><p>${escapeHTML(request.motivation)}</p></div>` : ""}
        ${request.review_note ? `<div class="beta-request-note"><strong>${state.language === "it" ? "Nota Admin" : "Admin note"}</strong><p>${escapeHTML(request.review_note)}</p></div>` : ""}
        <div class="quick-actions">
          ${!inviteUsed && request.status !== "blocked" ? `<button class="primary" data-beta-action="${request.status === "approved" ? "resend" : "approve"}" data-beta-request="${request.request_id}">${request.status === "approved" ? (state.language === "it" ? "Reinvia invito" : "Resend invite") : (state.language === "it" ? "Approva e invita" : "Approve and invite")}</button>` : ""}
          ${request.status === "pending" ? `<button class="secondary" data-beta-action="reject" data-beta-request="${request.request_id}">${state.language === "it" ? "Rifiuta" : "Reject"}</button>` : ""}
          ${!inviteUsed && request.status !== "blocked" ? `<button class="ghost" data-beta-action="block" data-beta-request="${request.request_id}">${state.language === "it" ? "Blocca" : "Block"}</button>` : ""}
        </div>
      </article>`;
    }).join("")
    : `<section class="card empty-state"><div class="empty-icon">✉</div><h2>${state.language === "it" ? "Nessuna richiesta beta" : "No beta requests"}</h2><p>${state.language === "it" ? "Le richieste inviate dalla pagina di accesso compariranno qui." : "Requests submitted from the login page will appear here."}</p></section>`;
  return `<div class="stack admin-beta-page">
    ${adminWorkspaceHero({
      icon: "✉",
      eyebrow: "Private beta",
      title: state.language === "it" ? "Richieste e inviti personali" : "Requests and personal invitations",
      description: state.language === "it" ? "Approva solo le persone che vuoi ammettere. Ogni invito è vincolato all’email, monouso e valido sette giorni." : "Approve only the people you want to admit. Every invitation is email-bound, single-use and valid for seven days.",
      metrics: [
        { label: state.language === "it" ? "in attesa" : "pending", value: pending },
        { label: state.language === "it" ? "approvate" : "approved", value: approved },
        { label: state.language === "it" ? "registrate" : "registered", value: completed, tone: "accent" }
      ]
    })}
    <section class="card beta-refresh-toolbar" aria-live="polite">
      <div>
        <span class="beta-refresh-icon" aria-hidden="true">↻</span>
        <div><strong>${state.language === "it" ? "Elenco richieste aggiornabile" : "Refreshable request list"}</strong><small>${state.language === "it" ? `Ultimo aggiornamento: ${refreshedAt}` : `Last refreshed: ${refreshedAt}`}</small></div>
      </div>
      <button class="secondary beta-refresh-button" id="refresh-beta-requests" type="button">↻ ${state.language === "it" ? "Aggiorna richieste" : "Refresh requests"}</button>
    </section>
    <section class="admin-kpi-grid">
      ${adminKpi(state.language === "it" ? "Richieste totali" : "Total requests", state.adminBetaRequests.length)}
      ${adminKpi(state.language === "it" ? "Da valutare" : "To review", pending, pending ? "warn" : "accent")}
      ${adminKpi(state.language === "it" ? "Inviti approvati" : "Approved invitations", approved)}
      ${adminKpi(state.language === "it" ? "Account creati" : "Created accounts", completed, "accent")}
    </section>
    <section class="beta-request-list">${requestCards}</section>
  </div>`;
}

function manageBetaRequestModal(requestID, action) {
  const request = state.adminBetaRequests.find(item => item.request_id === requestID);
  if (!request) return;
  const approving = action === "approve" || action === "resend";
  const title = approving
    ? (action === "resend" ? (state.language === "it" ? "Reinvia invito" : "Resend invitation") : (state.language === "it" ? "Approva accesso beta" : "Approve beta access"))
    : (action === "block" ? (state.language === "it" ? "Blocca richiesta" : "Block request") : (state.language === "it" ? "Rifiuta richiesta" : "Reject request"));
  showModal(`<div class="modal-header"><h2>${title}</h2><button class="close-button" data-close-modal>×</button></div>
    <p>${escapeHTML(request.first_name)} ${escapeHTML(request.last_name)} · <strong>${escapeHTML(request.email)}</strong></p>
    <form id="manage-beta-request-form" class="form-grid">
      <label>${state.language === "it" ? "Nota amministrativa (facoltativa)" : "Administrative note (optional)"}<textarea name="note" rows="3" maxlength="500">${escapeHTML(request.review_note || "")}</textarea></label>
      <div class="feedback">${approving
        ? (state.language === "it" ? "Verrà creato e inviato un link personale, utilizzabile una sola volta e valido 7 giorni." : "A personal, single-use link valid for 7 days will be created and sent.")
        : (state.language === "it" ? "L’utente non potrà creare un account con questa richiesta." : "The user will not be able to create an account with this request.")}</div>
      <button class="${approving ? "primary" : "secondary"} full" type="submit">${title}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#manage-beta-request-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector(".form-message");
    setBusy(button, true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/manage-beta-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY, Authorization: `Bearer ${sessionData.session?.access_token || ""}` },
        body: JSON.stringify({ request_id: requestID, action, note: new FormData(form).get("note") }),
      });
      const result = await readApiResponse(response, state.language === "it" ? "Operazione non riuscita." : "Operation failed.");
      if (result.invitation_url) {
        showModal(`<div class="modal-header"><h2>${result.email_sent ? (state.language === "it" ? "Invito inviato" : "Invitation sent") : (state.language === "it" ? "Invito creato" : "Invitation created")}</h2><button class="close-button" data-close-modal>×</button></div><p>${result.email_sent ? (state.language === "it" ? "L’email è stata inviata correttamente." : "The email was sent successfully.") : escapeHTML(result.email_message || "Email not sent.")}</p><label>Link<input id="beta-invitation-url" value="${escapeHTML(result.invitation_url)}" readonly></label><button class="primary full" id="copy-beta-invitation">${state.language === "it" ? "Copia link" : "Copy link"}</button>`);
        document.querySelector("#copy-beta-invitation").addEventListener("click", async () => {
          await navigator.clipboard.writeText(result.invitation_url);
          showToast(state.language === "it" ? "Link copiato." : "Link copied.");
        });
      } else {
        closeModal();
        showToast(state.language === "it" ? "Richiesta aggiornata." : "Request updated.");
      }
      await loadAdminStatistics();
      if (!result.invitation_url) adminView();
    } catch (error) {
      message.textContent = readableError(error, state.language === "it" ? "Operazione non riuscita." : "Operation failed.");
      setBusy(button, false);
    }
  });
}

function adminExercisesView() {
  const canonicalExercises = dedupeCanonicalExerciseRecords(state.adminExercises);
  const topicCount = new Set(canonicalExercises.map(exercise => canonicalExerciseDescriptor(exercise).topicCode)).size;
  const questionCount = canonicalExercises.reduce((sum, exercise) => sum + exerciseQuestionCount(exercise), 0);
  return `<div class="stack admin-exercises-page">
    ${adminWorkspaceHero({
      icon: "✎",
      eyebrow: state.language === "it" ? "Contenuti didattici" : "Learning content",
      title: state.language === "it" ? "Catalogo master BrainMath" : "BrainMath master catalogue",
      description: state.language === "it" ? "Organizza i contenuti per grado, argomento e difficoltà oppure amplia il catalogo tramite importazione CSV." : "Organise content by grade, topic and difficulty, or expand the catalogue with CSV imports.",
      metrics: [
        { label: state.language === "it" ? "esercizi" : "exercises", value: canonicalExercises.length.toLocaleString() },
        { label: state.language === "it" ? "argomenti" : "topics", value: topicCount.toLocaleString() },
        { label: state.language === "it" ? "domande" : "questions", value: questionCount.toLocaleString(), tone: "accent" }
      ]
    })}
    <section class="card admin-workspace-card admin-exercise-catalog">
    <div class="workspace-section-title">
      <div>
        <span class="eyebrow">${state.language === "it" ? "Libreria didattica" : "Learning library"}</span>
        <h2>${state.language === "it" ? "Gestione esercizi" : "Exercise management"}</h2>
        <p style="color:var(--muted)">${state.language === "it" ? "Catalogo master separato dalla dashboard principale." : "Master catalogue separated from the main dashboard."}</p>
      </div>
      <button class="secondary" id="import-admin-csv">${state.language === "it" ? "Importa CSV" : "Import CSV"}</button>
    </div>
    <div class="exercise-list">${curriculumBrowser(state.adminExercises, "admin")}</div>
  </section></div>`;
}

export function adminView() {
  const content = state.adminTab === "users"
    ? adminUsersView()
    : state.adminTab === "beta"
      ? adminBetaAccessView()
    : state.adminTab === "activity"
      ? adminActivityView()
    : state.adminTab === "exercises"
      ? adminExercisesView()
      : state.adminTab === "costs"
        ? adminCostsView()
        : adminDashboardView();
  const [pageTitle, pageSubtitle] = adminPageTitle();
  app.innerHTML = `
    <div class="app-shell admin-shell">
      <aside class="sidebar">
        <div class="brand"><img class="brand-logo small" src="/brainmath-logo.png" alt="">BrainMath</div>
        <div class="sidebar-profile admin-sidebar-title admin-identity-card">
          <span class="admin-shield">◆</span><div><strong>${state.language === "it" ? "Amministrazione" : "Administration"}</strong><span>${escapeHTML(SUPABASE_PROJECT_NAME)}</span><small>@${escapeHTML(state.account.username)}</small></div>
        </div>
        ${adminNavigation()}
        <div class="sidebar-footer">
          <button class="nav-button" type="button" data-admin-logout><span class="nav-icon">↩</span><span>${t("logout")}</span></button>
        </div>
      </aside>
      <main class="main-content admin-main">
        <header class="topbar admin-topbar professional-topbar">
          <div class="topbar-title-wrap">
            <span class="professional-page-mark">${({ dashboard: "▦", activity: "↗", users: "👥", beta: "✉", exercises: "✎", costs: "€" })[state.adminTab] || "▦"}</span>
            <div>
              <span class="workspace-eyebrow">${state.language === "it" ? "Centro di controllo" : "Control centre"}</span>
              <h1>${pageTitle}</h1>
              <p>${pageSubtitle}</p>
            </div>
          </div>
          <div class="admin-topbar-tools">
            <span class="system-status-pill"><i></i>${IS_LOCAL_DEVELOPMENT
              ? (state.language === "it" ? "Ambiente locale" : "Local environment")
              : (state.language === "it" ? "Beta privata" : "Private beta")}</span>
            ${languageControl()}
            <button class="secondary admin-exit-button" type="button" data-admin-logout aria-label="${t("logout")}">
              <span aria-hidden="true">↩</span><span>${t("logout")}</span>
            </button>
          </div>
        </header>
        <div class="admin-page-content">${content}</div>
      </main>
      <div class="mobile-nav admin-mobile-nav">${adminNavigation()}</div>
    </div>`;
  bindLanguage();
  document.querySelectorAll("[data-admin-logout]").forEach(button => {
    button.addEventListener("click", logout);
  });
  document.querySelectorAll("[data-admin-tab]").forEach(button => {
    button.addEventListener("click", () => {
      state.adminTab = button.dataset.adminTab;
      adminView();
    });
  });
  document.querySelector("#import-admin-csv")?.addEventListener("click", importExercisesModal);
  document.querySelectorAll("[data-beta-action]").forEach(button => {
    button.addEventListener("click", () => manageBetaRequestModal(button.dataset.betaRequest, button.dataset.betaAction));
  });
  document.querySelector("#refresh-beta-requests")?.addEventListener("click", async event => {
    const button = event.currentTarget;
    setBusy(button, true);
    try {
      await loadAdminBetaRequests();
      adminView();
      showToast(state.language === "it" ? "Richieste beta aggiornate." : "Beta requests refreshed.");
    } catch (error) {
      showToast(readableError(error, state.language === "it" ? "Impossibile aggiornare le richieste." : "Unable to refresh requests."));
      setBusy(button, false);
    }
  });
  if (state.adminTab === "exercises") bindCurriculumBrowser("admin", adminView);
  document.querySelectorAll("[data-toggle-catalog]").forEach(button => {
    button.addEventListener("click", async () => {
      const { error } = await supabase.rpc("set_catalog_exercise_availability", {
        selected_exercise: button.dataset.toggleCatalog,
        selected_available: button.dataset.available !== "true"
      });
      if (error) return showToast(error.message);
      await loadAdminStatistics();
      adminView();
      showToast(state.language === "it" ? "Disponibilità del catalogo aggiornata." : "Catalogue availability updated.");
    });
  });
  document.querySelector("#exchange-rate-form")?.addEventListener("submit", async event => {
    event.preventDefault();
    const form = event.currentTarget;
    const rate = Number(new FormData(form).get("rate"));
    const { error } = await supabase.rpc("update_admin_usd_to_eur", { selected_rate: rate });
    if (error) return form.querySelector(".form-message").textContent = error.message;
    await loadAdminStatistics();
    adminView();
  });
  document.querySelector("#platform-cost-form")?.addEventListener("submit", async event => {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    const amountCents = Math.round(Number(values.get("amount")) * 100);
    const { error } = await supabase.rpc("add_admin_platform_cost", {
      selected_cost_date: values.get("cost_date"),
      selected_category: values.get("category"),
      selected_provider: values.get("provider"),
      selected_description: values.get("description"),
      selected_amount_cents: amountCents,
      selected_currency: "eur"
    });
    if (error) return form.querySelector(".form-message").textContent = error.message;
    await loadAdminStatistics();
    adminView();
    showToast(state.language === "it" ? "Costo registrato." : "Cost saved.");
  });
  document.querySelector("#refresh-supabase-usage")?.addEventListener("click", async event => {
    setBusy(event.currentTarget, true);
    state.adminSupabaseUsage = await loadSupabaseUsageSnapshot();
    adminView();
  });
  document.querySelector("#refresh-platform-usage")?.addEventListener("click", async event => {
    setBusy(event.currentTarget, true);
    state.adminSupabaseUsage = await loadSupabaseUsageSnapshot();
    adminView();
  });
  document.querySelector("#prefill-domain-cost")?.addEventListener("click", () => {
    const form = document.querySelector("#platform-cost-form");
    if (!form) return;
    form.elements.category.value = "domain";
    form.elements.provider.value = "Namecheap";
    form.elements.description.value = "brainmathgame.com renewal";
    form.elements.amount.focus();
    showToast(state.language === "it" ? "Costo dominio precompilato: inserisci solo l’importo." : "Domain cost prefilled: just enter the amount.");
  });
  document.querySelector("#prefill-email-cost")?.addEventListener("click", () => {
    const form = document.querySelector("#platform-cost-form");
    if (!form) return;
    form.elements.category.value = "email";
    form.elements.provider.value = "Resend";
    form.elements.description.value = "Transactional emails";
    form.elements.amount.focus();
    showToast(state.language === "it" ? "Costo email precompilato: inserisci solo l’importo." : "Email cost prefilled: just enter the amount.");
  });
}
